/**
 * Gate.io timeframe configuration and interval mapping.
 * @see https://www.gate.com/docs/developers/apiv4/en/#market-k-line-chart
 *
 * Supported intervals: 1s, 10s, 1m, 5m, 15m, 30m, 1h, 4h, 8h, 1d, 7d, 30d
 * Note: 30s is NOT supported by Gate.io API
 */

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * Gate.io supports 1-second native interval.
 * Supported intervals: 1s, 10s, 1m, 5m, 15m, 30m, 1h, 4h, 8h, 1d, 7d, 30d
 */
export const GATE_TIMEFRAMES: TimeframeConfig = {
  native: ['1s', '10s', '1m', '5m', '15m', '30m', '1h', '4h', '8h', '1d', '7d', '30d'],
  synthetic: [],
  minIntervalSeconds: 1,
};

// Register with central registry
registerExchangeTimeframes('gate', GATE_TIMEFRAMES);

/**
 * Maps TradingView resolution to Gate interval format.
 * Returns closest supported interval if exact match not available.
 *
 * @see https://www.gate.com/docs/developers/apiv4/en/#market-k-line-chart
 */
export const mapToGateInterval = (resolution: string): string => {
  const seconds = resolutionToSeconds(resolution);

  if (seconds === 1) return '1s';
  if (seconds === 10) return '10s';
  // Note: 30s is NOT supported by Gate.io API - fall through to 1m
  if (seconds === 60) return '1m';
  if (seconds === 300) return '5m';
  if (seconds === 900) return '15m';
  if (seconds === 1800) return '30m';
  if (seconds === 3600) return '1h';
  if (seconds === 14400) return '4h';
  if (seconds === 28800) return '8h';
  if (seconds === 86400) return '1d';
  if (seconds === 604800) return '7d';
  if (seconds === 2592000) return '30d';

  // For unsupported intervals, map to the next higher supported interval
  // This ensures data aggregation is accurate (using fewer, larger candles rather than more, smaller ones)
  if (seconds < 10) return '1s';
  // 11-59 seconds (including 30s) -> use 1m (next higher supported)
  if (seconds < 60) return '1m';
  if (seconds < 300) return '1m';
  if (seconds < 900) return '5m';
  if (seconds < 1800) return '15m';
  if (seconds < 3600) return '30m';
  if (seconds < 14400) return '1h';
  if (seconds < 28800) return '4h';
  if (seconds < 86400) return '8h';
  if (seconds < 604800) return '1d';
  if (seconds < 2592000) return '7d';

  // For intervals >= 30 days, use the largest supported interval
  return '30d';
};
